import { request } from '@umijs/max';

export interface UserProfileData {
  id: number;
  name: string;
  email: string;
  cedula: string;
  fk_rol: number;
  status: boolean;
}

export interface UserUpdateData {
  name?: string;
  email?: string;
  cedula?: string;
  previous_password?: string;
  password?: string;
  fk_rol?: number;
  status?: boolean;
}

export interface UserPreferences {
  theme: 'light' | 'dark';
  language: string;
  notifications: {
    email: boolean;
    browser: boolean;
    desktop: boolean;
  };
  dashboard: {
    compactMode: boolean;
    showWelcome: boolean;
  };
}

class UserConfigService {
  private readonly API_BASE_URL: string;
  // In-memory cache: userId -> dataUrl|null
  private photoCache: Record<number, string | null> = {};

  constructor() {
    this.API_BASE_URL = process.env.UMI_APP_API_URL || 'http://localhost:1337';
  }

  /**
   * Obtiene el perfil del usuario actual
   */
  async getUserProfile(userId: number): Promise<UserProfileData> {
    try {
      const response = await request(`${this.API_BASE_URL}/user/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.status !== 200) {
        throw new Error(response.message || 'Error al obtener el perfil');
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Actualiza el perfil del usuario
   */
  async updateUserProfile(userId: number, data: UserUpdateData): Promise<UserProfileData> {
    try {
      const response = await request(`${this.API_BASE_URL}/user/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        data,
      });

      if (response.status !== 200) {
        throw new Error(response.message || 'Error al actualizar el perfil');
      }

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cambia la contraseña del usuario
   */
  async changePassword(userId: number, currentPassword: string, newPassword: string): Promise<void> {
    try {
      // New dedicated endpoint for changing password
      const response = await request(`${this.API_BASE_URL}/user/change-password`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        data: {
          fk_user: userId,
          previous_password: currentPassword,
          password: newPassword,
        },
      });

      if (response && response.status && response.status !== 200) {
        throw new Error(response.message || 'Error al cambiar la contraseña');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sube/actualiza la foto de perfil de un usuario. Se envía FormData (user_id, photo)
   */
  async uploadUserPhoto(targetUserId: number, file: File): Promise<void> {
    try {
      const formData = new FormData();
  // Some backends expect the field to be named 'id' while others expect 'user_id'.
  // Include both for compatibility. Values are strings in multipart forms.
  formData.append('id', String(targetUserId));
  formData.append('user_id', String(targetUserId));
      formData.append('photo', file);

      // Use fetch directly so the browser sets the multipart boundary automatically
      const token = localStorage.getItem('token');
  // Send id also as query parameter (some server validators expect numeric param in URL)
  const resp = await fetch(`${this.API_BASE_URL}/user/update-photo?id=${encodeURIComponent(String(targetUserId))}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!resp.ok) {
        // Try parse JSON error first for clearer message
        let errText = await resp.text();
        try {
          const json = JSON.parse(errText);
          errText = json?.message ? JSON.stringify(json, null, 2) : JSON.stringify(json);
        } catch (_e) {
          // leave errText as raw text
        }
        throw new Error(errText || 'Error al subir la foto');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtiene la foto de un usuario como data URL (fetch blob y convertir a base64 URL).
   * Esto permite usar la imagen en un <img> o <Avatar src="..." /> incluso si el endpoint requiere Authorization.
   */
  async getUserPhotoDataUrl(userId: number): Promise<string | null> {
    // Return cached value if present
    try {
      if (this.photoCache.hasOwnProperty(userId)) return this.photoCache[userId] as string | null;

      // Try localStorage first (persisted cache)
      try {
        const key = `user_photo_${userId}`;
        const stored = localStorage.getItem(key);
        if (stored) {
          this.photoCache[userId] = stored;
          return stored;
        }
      } catch (e) {
        // ignore localStorage errors
      }

      const token = localStorage.getItem('token');
      const resp = await fetch(`${this.API_BASE_URL}/user/photo/${userId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!resp.ok) {
        this.photoCache[userId] = null;
        return null;
      }

      const blob = await resp.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Cache in memory and localStorage
      this.photoCache[userId] = dataUrl;
      try {
        localStorage.setItem(`user_photo_${userId}`, dataUrl);
      } catch (e) {
        // ignore localStorage quota/errors
      }

      return dataUrl;
    } catch (error) {
      return null;
    }
  }

  
  invalidateUserPhoto(userId: number) {
    try {
      delete this.photoCache[userId];
    } catch (e) {}
    try {
      localStorage.removeItem(`user_photo_${userId}`);
    } catch (e) {}
  }

  /**
   * Obtiene las preferencias del usuario (almacenadas en localStorage)
   */
  getUserPreferences(): UserPreferences {
    const defaultPreferences: UserPreferences = {
      theme: 'light',
      language: 'es',
      notifications: {
        email: true,
        browser: true,
        desktop: false,
      },
      dashboard: {
        compactMode: false,
        showWelcome: true,
      },
    };

    try {
      const stored = localStorage.getItem('crm-user-preferences');
      return stored ? { ...defaultPreferences, ...JSON.parse(stored) } : defaultPreferences;
    } catch (error) {
      return defaultPreferences;
    }
  }

  /**
   * Guarda las preferencias del usuario
   */
  setUserPreferences(preferences: Partial<UserPreferences>): void {
    try {
      const current = this.getUserPreferences();
      const updated = { ...current, ...preferences };
      localStorage.setItem('crm-user-preferences', JSON.stringify(updated));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Resetea las preferencias a los valores por defecto
   */
  resetUserPreferences(): void {
    localStorage.removeItem('crm-user-preferences');
    localStorage.removeItem('crm-theme');
  }
}

export default new UserConfigService();
